/**
 * @fileoverview resolveLucideIcon — the ONE closed icon registry's resolvers.
 *
 * String icon identities (nav `iconName`s, dashboard group/widget icons, command-palette actions,
 * the `<Icon name>` adapters, SKL-004 skill icons) resolve to mounted Lucide React icons HERE,
 * through the closed map in `iconMap.jsx` — see that module's docblock for why the map is closed
 * (tree-shaking) and why it is ONE (PLANNER-BOARD-UX-003 U22). Moved here from
 * `@puffin-core/page-shell` (DCS-001 / Phase E1.0, 2026-04-27; widened by PERF-FECSS-001 P14,
 * 2026-08-02; the warning by NAV-ICON-COVERAGE-001, 2026-09-03) so that page-shell, command-palette
 * (which page-shell depends on — a cycle the other way) and every app read one registry.
 *
 * 🔴 NAV-ICON-COVERAGE-001 (2026-09-03). page-shell's resolver once asserted "the P14 icon-coverage
 * contract asserts every fleet nav-config `iconName` resolves here." No such contract existed, and
 * `AppSidebar` resolves with `fallback: null`, so an unregistered name rendered NOTHING: no icon, no
 * warning, no lint error, no red test — measured at close, 103 of 275 fleet nav items (37%) rendered
 * no icon, each a 48x48 blank but clickable target in rail mode. pos-web was the one clean app
 * because its own `<Icon>` adapter WARNED on a miss. That warning is the whole difference, and it is
 * why `warnUnregisteredIcon` below exists. The contract is real now:
 * `tests/cross-app/contracts/appShellNavConfig.contract.test.js`.
 *
 * @module @puffin-core/icon-registry/resolveLucideIcon
 */

import { LayoutGrid } from 'lucide-react';

import { ICON_MAP } from './iconMap.jsx';

const DEFAULT_SIZE = 16;

/**
 * Names already warned about, so a re-rendering sidebar logs each bad name
 * once per session instead of once per render.
 */
const WARNED_ICON_NAMES = new Set();

/**
 * Look a name up as an OWN property of the registry.
 *
 * `ICON_MAP` is a plain frozen object, so a bare `ICON_MAP[name]` resolves
 * `constructor`, `toString` and `valueOf` through the prototype chain — all
 * truthy, none a React component. pos-web's `<Icon>` closed this same hole
 * (ES-I18N-FOLLOW-ON Q-30); a closed registry that resolves inherited members
 * is not actually closed.
 *
 * @param {*} name
 * @returns {import('lucide-react').LucideIcon|undefined}
 */
function lookupIcon(name) {
  return typeof name === 'string' && Object.prototype.hasOwnProperty.call(ICON_MAP, name)
    ? ICON_MAP[name]
    : undefined;
}

/**
 * Warn once per unregistered icon name.
 *
 * Deliberately NOT gated on `NODE_ENV`, and that is the considered choice: an
 * unregistered name is a DEFECT, never a normal state, so it is worth knowing
 * about in production too — and pos-web's `<Icon>`, the one app whose nav
 * never drifted, warns unconditionally for exactly that reason. The warning is
 * the whole difference between the two registries' outcomes. `console.warn` is
 * allowed here by this package's `no-console` config, with precedent in
 * `useCrossAppLaunch` / `useResource`.
 *
 * (A WORKING dev gate does now exist in this package — the bare literal
 * `process.env.NODE_ENV` plus a `/* global process *\/` declaration, used by
 * `defineDashboardWidget.jsx` and `useResource.js`. It is deliberately not
 * used here. An earlier revision of this docblock claimed no working gate
 * existed, which was true only because both of those guards were themselves
 * broken; they were fixed in the same initiative. Do not "restore consistency"
 * by gating this warning — the choice is about severity, not mechanism.)
 *
 * A blank/absent name is NOT a defect — callers legitimately pass `null` for
 * "no icon" — so only a non-empty string that misses is reported.
 *
 * @param {*} name
 * @returns {void}
 */
function warnUnregisteredIcon(name) {
  if (typeof name !== 'string' || name === '' || WARNED_ICON_NAMES.has(name)) return;
  WARNED_ICON_NAMES.add(name);
  console.warn(
    `[icon-registry] Icon "${name}" is not in the closed icon registry, so it renders ` +
      'nothing. Add it to packages/icon-registry/lib/esm/iconMap.jsx (import + ICON_MAP) and ' +
      'iconNames.js. See NAV-ICON-COVERAGE-001.',
  );
}

/**
 * Resolve an icon name to its Lucide component reference, or null.
 *
 * The component-reference form for callers that mount the icon themselves
 * (DashboardWidget's EmptyState contract takes an elementType).
 *
 * @param {string} name - Icon name from the closed registry
 * @returns {import('lucide-react').LucideIcon|null} the Lucide component, or null for unknown names
 */
export function resolveLucideIconComponent(name) {
  const Icon = lookupIcon(name);
  if (!Icon) warnUnregisteredIcon(name);
  return Icon || null;
}

/**
 * Resolve an icon name to a mounted Lucide element.
 *
 * @param {string} name - Icon name from the closed registry
 * @param {Object} [options]
 * @param {number} [options.size=16] - Icon pixel size
 * @param {boolean} [options.ariaHidden=true] - Forwarded aria-hidden
 * @param {React.ComponentType<import('lucide-react').LucideProps>|null} [options.fallback=LayoutGrid] -
 *   Component used for unknown names. Pass `null` to render nothing instead (the nav surfaces'
 *   contract: an unknown iconName renders no icon rather than a sentinel).
 * @param {string} [options.className] - Forwarded to the icon when set (see below).
 * @returns {React.JSX.Element|null}
 */
export function resolveLucideIcon(name, options = {}) {
  const { size = DEFAULT_SIZE, ariaHidden = true, fallback = LayoutGrid, className } = options;
  const registered = lookupIcon(name);
  if (!registered) warnUnregisteredIcon(name);
  const Icon = registered || fallback;
  if (!Icon) {
    return null;
  }
  // Lucide icons default to `aria-hidden="true"`. We forward the prop
  // explicitly so callers can opt in to a labeled icon (`ariaHidden=false`),
  // which suppresses Lucide's default and exposes the SVG to AT.
  // `className` (PLANNER-BOARD-UX-003 U14, 2026-10-08) lets a list row that styles the glyph
  // itself (palm's More hub, `.menu-list-item-icon`) resolve by NAME like the shell does, so a nav
  // tree carries ONE icon representation — the registry name — for every renderer.
  return <Icon size={size} aria-hidden={ariaHidden} {...(className ? { className } : {})} />;
}
